import { useState } from 'react';
import { History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditHistory } from '@/services/auditService';
import type { AuditChange, AuditEntityType, AuditEntry } from '@/types';
import { formatCalendarDate, formatDate } from '@/utils/formatters';

const ACTION_LABELS: Record<AuditEntry['action'], string> = {
  Create: 'Created',
  Update: 'Updated',
  Delete: 'Deleted',
  Archive: 'Archived',
  View: 'Viewed',
};

const ENTITY_LABELS: Record<AuditEntityType, string> = {
  Contract: 'Contract',
  Vendor: 'Vendor',
  License: 'License',
  LicenseAllocation: 'Allocation',
  Document: 'Document',
  CostRecord: 'Cost record',
  ContractDate: 'Milestone',
};

const FIELD_LABELS: Record<string, string> = {
  annualContractValue: 'Annual value',
  contractId: 'Contract',
  contractNumber: 'Contract number',
  costPerUnit: 'Cost per unit',
  dateType: 'Type',
  departmentId: 'Department',
  licenseId: 'License',
  noticeRequiredDays: 'Notice days',
  originalFileName: 'File name',
  ownerUserId: 'Owner',
  parentContractId: 'Parent contract',
  totalContractValue: 'Total value',
  vendorId: 'Vendor',
};

/** Calendar days and instants among the recorded fields (#65); everything else is shown as written */
const DATE_ONLY_FIELDS = new Set(['date', 'endDate', 'expirationDate', 'renewalDate', 'startDate']);
const INSTANT_FIELDS = new Set(['allocatedDate', 'deallocatedDate']);

/** Fields holding the id of another row; the API names the row, and only a row that is gone has no name */
const REFERENCE_FIELDS = new Set(['contractId', 'departmentId', 'licenseId', 'ownerUserId', 'parentContractId', 'vendorId']);

const fieldLabel = (field: string): string => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const words = field.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatValue = (field: string, value: AuditChange['newValue'], display: string | null | undefined): string => {
  if (display) return display;
  if (value === null || value === undefined) return 'empty';
  if (REFERENCE_FIELDS.has(field)) return 'a removed record';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'string') return String(value);
  if (DATE_ONLY_FIELDS.has(field)) return formatCalendarDate(value);
  if (INSTANT_FIELDS.has(field)) return formatDate(value, 'MMM dd, yyyy HH:mm');
  return value;
};

const sourceLabel = (source: string | null | undefined): string | null => {
  if (!source) return null;
  if (source.startsWith('extraction:')) return 'Document review';
  if (source === 'import') return 'Import';
  if (source === 'demo-cleanup') return 'Demo cleanup';
  return source;
};

const ChangeLine = ({ change }: { change: AuditChange }) => (
  <li className="break-words">
    <span className="font-medium text-grey-700">{fieldLabel(change.field)}:</span>{' '}
    <span className="text-grey-500">{formatValue(change.field, change.oldValue, change.oldDisplay)}</span>
    {' → '}
    <span className="text-grey-900">{formatValue(change.field, change.newValue, change.newDisplay)}</span>
  </li>
);

const ValueLine = ({ change, side }: { change: AuditChange; side: 'old' | 'new' }) => (
  <li className="break-words">
    <span className="font-medium text-grey-700">{fieldLabel(change.field)}:</span>{' '}
    {side === 'new'
      ? formatValue(change.field, change.newValue, change.newDisplay)
      : formatValue(change.field, change.oldValue, change.oldDisplay)}
  </li>
);

/** A create's or delete's full values, rendered only once opened: most rows are never read */
const RecordedValues = ({ entry }: { entry: AuditEntry }) => {
  const [open, setOpen] = useState(false);
  const count = entry.changes.length;
  return (
    <details className="mt-2 text-sm" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer text-grey-500">
        {count} {count === 1 ? 'field' : 'fields'}
      </summary>
      {open && (
        <ul className="mt-1 space-y-1">
          {entry.changes.map((change) => (
            <ValueLine key={change.field} change={change} side={entry.action === 'Create' ? 'new' : 'old'} />
          ))}
        </ul>
      )}
    </details>
  );
};

const AuditRow = ({ entry, rootType }: { entry: AuditEntry; rootType: AuditEntityType }) => {
  const source = sourceLabel(entry.source);
  const isChild = entry.entityType !== rootType;
  return (
    <li data-testid="audit-row" className="border-b border-border py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-grey-900">{ACTION_LABELS[entry.action] ?? entry.action}</span>
        {isChild && (
          <span className="text-grey-700">
            {ENTITY_LABELS[entry.entityType] ?? entry.entityType}
            {entry.entityLabel ? ` ${entry.entityLabel}` : ''}
          </span>
        )}
        {source && <Badge variant="outline">{source}</Badge>}
      </div>
      <p className="mt-1 break-words text-xs text-grey-500">
        {formatDate(entry.timestamp, 'MMM dd, yyyy HH:mm')} · {entry.userEmail}
      </p>
      {(entry.action === 'Update' || entry.action === 'Archive') && (
        <ul className="mt-2 space-y-1 text-sm">
          {entry.changes.map((change) => <ChangeLine key={change.field} change={change} />)}
        </ul>
      )}
      {(entry.action === 'Create' || entry.action === 'Delete') && entry.changes.length > 0 && <RecordedValues entry={entry} />}
    </li>
  );
};

interface AuditHistoryCardProps {
  entityType: AuditEntityType;
  entityId: string;
  /** Also show what happened to the entity's children, which have no page of their own */
  includeChildren?: boolean;
}

/** Who changed what on an entity. Admin only, like the endpoint behind it (#30) */
export const AuditHistoryCard = ({ entityType, entityId, includeChildren = false }: AuditHistoryCardProps) => {
  const { isAdmin } = useAuth();
  const history = useAuditHistory({ entityType, entityId, includeChildren }, isAdmin);

  if (!isAdmin) return null;

  // Pages are by offset: a row added between "Show more" clicks repeats the last row of the page before
  const entries = [...new Map(history.data?.pages.flatMap((page) => page.items).map((entry) => [entry.id, entry])).values()];

  return (
    <Card data-testid="audit-history">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" aria-hidden="true" />
          History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {history.isPending && <Skeleton className="h-20 w-full" />}
        {history.isError && <p className="text-sm text-grey-500">The history could not be loaded.</p>}
        {history.isSuccess && entries.length === 0 && (
          <p className="text-sm text-grey-500">No changes recorded yet.</p>
        )}
        {entries.length > 0 && (
          <ul>
            {entries.map((entry) => <AuditRow key={entry.id} entry={entry} rootType={entityType} />)}
          </ul>
        )}
        {history.hasNextPage && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => history.fetchNextPage()} disabled={history.isFetchingNextPage}>
            {history.isFetchingNextPage ? 'Loading...' : 'Show more'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
